import { FILTER } from '@fiverr-private/listing_lib';
import type { ActiveSideFiltersType, FilterType, SelectedFilter } from '@fiverr-private/listing_types';
import { isSimilarTimezoneActive } from '../../../../../utils/timezoneFilter';
import { TOP_GROUPING } from '../../../../../config/filters';
import { MENU_CONTENT_TYPES } from '../../menus/consts';
import { filterAndSort, isActive, isOnlineNowSelected } from '../../utils';
import { getFilterById } from '../../../util/getFilterById';
import { FREELANCER_TYPE_CHIP_ID } from '../../../side_filters/ActiveFiltersSection/ActiveFilter/ActiveFilter.config';
import { appTranslate } from '../../../../../../../shared/utils/i18n';
import { CATEGORY_SPECIFIC_EXPOSURE } from '../FilterRow.constants';
import type { CategoryExposure, FilterRowItemData, FiltersModalTriggerProps } from '../FilterRow.types';
import { buildCategoryControls } from './buildCategoryControls';
import { buildPillDropdownProps } from './buildPillDropdownProps';

const TOGGLE_SOURCE = 'filter_row';

const BASE_CHIP_EXCLUDED_FILTER_IDS = [
    FILTER.PRO.ID,
    FILTER.IS_SELLER_ONLINE.ID,
    FILTER.GIG_PRICE_RANGE.ID,
    FILTER.PRICE_BUCKETS.ID,
];

// Every chip shows just its label, except these two which keep a "Location: " / "Language: " prefix.
const CHIP_PREFIX_COPIES: Record<string, Parameters<typeof appTranslate>[0]> = {
    [FILTER.SELLER_LOCATION.ID]: 'search_perseus.side_filters.filter_row_location_prefix',
    [FILTER.SELLER_LANGUAGE.ID]: 'search_perseus.side_filters.filter_row_language_prefix',
};

// Merged into one chip because the modal shows them as a single "Talent quality" group.
const FREELANCER_TYPE_CHIP_FILTER_IDS = [FILTER.IS_AGENCY.ID, FILTER.SELLER_LEVEL.ID, FILTER.SELLER_TIER.ID];

// Keeps an active pill visible when the backend returns no is_seller_online facet.
const ACTIVE_ONLINE_NOW_FILTER = {
    id: FILTER.IS_SELLER_ONLINE.ID,
    display_type: 'checkbox',
    options: [{ id: 'true', selected: true }],
} as FilterType;

interface BuildFilterRowItemsParams {
    filters: FilterType[];
    activeFilters: ActiveSideFiltersType;
    selectedFiltersHierarchy: SelectedFilter[];
    currencySymbol: string;
    filtersModalProps: FiltersModalTriggerProps;
    categoryExposure?: CategoryExposure;
}

const buildBudgetDropdown = (
    budgetFilters: FilterType[],
    selectedFiltersHierarchy: SelectedFilter[],
    activeFilters: ActiveSideFiltersType,
    currencySymbol: string
): FilterRowItemData => {
    const selectedBudget = selectedFiltersHierarchy.find(
        ({ id }) => id === FILTER.GIG_PRICE_RANGE.ID || id === FILTER.PRICE_BUCKETS.ID
    );
    const selectedBudgetValue = selectedBudget?.values[0];

    return {
        type: 'dropdown',
        rowKey: 'dropdown-budget',
        props: {
            ...buildPillDropdownProps(currencySymbol, activeFilters),
            title:
                selectedBudgetValue?.activeFilterAlias ||
                selectedBudgetValue?.alias ||
                appTranslate('search_perseus.filters.global.budget.alias'),
            groupName: TOP_GROUPING.BUDGET,
            active: isActive(budgetFilters, activeFilters),
            contentType: MENU_CONTENT_TYPES.BUDGET,
        },
    };
};

const buildToggleItems = (filters: FilterType[], activeFilters: ActiveSideFiltersType): FilterRowItemData[] => {
    const items: FilterRowItemData[] = [];
    const proFilter = getFilterById(filters, FILTER.PRO.ID);
    const onlineNowFilter =
        getFilterById(filters, FILTER.IS_SELLER_ONLINE.ID) ??
        (isOnlineNowSelected(activeFilters) ? ACTIVE_ONLINE_NOW_FILTER : undefined);

    if (proFilter) {
        items.push({
            type: 'proToggle',
            rowKey: 'proToggle',
            props: { filter: proFilter, tooltip: true, source: TOGGLE_SOURCE, usePillButton: true },
        });
    }

    items.push({
        type: 'similarTimezoneToggle',
        rowKey: 'similarTimezoneToggle',
        props: { active: isSimilarTimezoneActive(activeFilters) },
    });

    if (onlineNowFilter) {
        items.push({
            type: 'onlineNowToggle',
            rowKey: 'onlineNowToggle',
            props: { filter: onlineNowFilter, tooltip: true, source: TOGGLE_SOURCE, usePillButton: true },
        });
    }

    return items;
};

const buildChipItems = (
    selectedFiltersHierarchy: SelectedFilter[],
    excludedFilterIds: string[],
    activeFilters: ActiveSideFiltersType
): FilterRowItemData[] => {
    const visibleFilters = selectedFiltersHierarchy.filter(({ id }) => !excludedFilterIds.includes(id));
    const freelancerTypeFilters = visibleFilters.filter(({ id }) => FREELANCER_TYPE_CHIP_FILTER_IDS.includes(id));

    return visibleFilters.flatMap((filter): FilterRowItemData[] => {
        if (filter.id === FILTER.SERVICE_OFFERINGS.ID) {
            // Subscription and video consultations are separate active filters, so each gets its own pill.
            return filter.values.map((value) => ({
                type: 'chip',
                rowKey: `chip-${value.id}`,
                props: { id: value.id, alias: '', values: [value], activeFilters },
            }));
        }

        if (!FREELANCER_TYPE_CHIP_FILTER_IDS.includes(filter.id)) {
            const { id, values } = filter;
            const prefixCopy = CHIP_PREFIX_COPIES[id];
            const alias = prefixCopy ? String(appTranslate(prefixCopy)) : '';

            return [{ type: 'chip', rowKey: `chip-${id}`, props: { id, alias, values, activeFilters } }];
        }

        if (filter !== freelancerTypeFilters[0]) {
            return [];
        }

        return [
            {
                type: 'chip',
                rowKey: `chip-${FREELANCER_TYPE_CHIP_ID}`,
                props: {
                    id: FREELANCER_TYPE_CHIP_ID,
                    alias: '',
                    values: freelancerTypeFilters.flatMap(({ values }) => values),
                    activeFilters,
                },
            },
        ];
    });
};

export const buildFilterRowItems = ({
    filters,
    activeFilters,
    selectedFiltersHierarchy,
    currencySymbol,
    filtersModalProps,
    categoryExposure = CATEGORY_SPECIFIC_EXPOSURE,
}: BuildFilterRowItemsParams): FilterRowItemData[] => {
    const budgetFilters = filterAndSort(filters, TOP_GROUPING.BUDGET);
    const categoryControls = buildCategoryControls({ categoryExposure, filters, activeFilters, currencySymbol });
    const chipExcludedFilterIds = [...BASE_CHIP_EXCLUDED_FILTER_IDS, ...categoryControls.shownFilterIds];

    return [
        { type: 'filterTrigger', rowKey: 'filterTrigger', props: { ...filtersModalProps, hideDivider: true } },
        ...(budgetFilters.length > 0
            ? [buildBudgetDropdown(budgetFilters, selectedFiltersHierarchy, activeFilters, currencySymbol)]
            : []),
        ...categoryControls.dropdowns,
        { type: 'divider', rowKey: 'divider', props: undefined },
        ...categoryControls.options,
        ...buildToggleItems(filters, activeFilters),
        ...buildChipItems(selectedFiltersHierarchy, chipExcludedFilterIds, activeFilters),
    ];
};
